// Canvas 2D frame: background grid, world bounds, then every entity through its module `draw`.
// The module draws in world units; the runtime owns the transform.

import type { ClientWorld, Entity, GameModule } from "../contract.js";
import type { Camera } from "./camera.js";

const GRID = 200;

/**
 * Draw order: by the type's `layer` (lower first, so it ends up underneath), then static
 * entities before dynamic ones (scenery under actors, whatever their sizes), then by radius
 * (small first, so big things draw on top), then by id so the order is stable.
 */
export function drawOrder(entities: Entity[], game: GameModule): Entity[] {
  const layer = (e: Entity) => game.entities.get(e.type)?.layer ?? 0;
  const moving = (e: Entity) => (game.entities.get(e.type)?.static ? 0 : 1);
  return entities.slice().sort((a, b) => layer(a) - layer(b) || moving(a) - moving(b) || a.r - b.r || a.id - b.id);
}

export function drawFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  dpr: number,
  camera: Camera,
  entities: Entity[],
  game: GameModule,
  world: ClientWorld,
  alpha: number,
): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = "#101418";
  ctx.fillRect(0, 0, w, h);

  camera.apply(ctx, w, h, dpr);
  const view = camera.rect(w, h);

  // Grid, clipped to the world.
  const gx0 = Math.max(0, Math.floor(view.x0 / GRID) * GRID);
  const gx1 = Math.min(world.w, Math.ceil(view.x1 / GRID) * GRID);
  const gy0 = Math.max(0, Math.floor(view.y0 / GRID) * GRID);
  const gy1 = Math.min(world.h, Math.ceil(view.y1 / GRID) * GRID);
  ctx.lineWidth = 1 / camera.zoom;
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.beginPath();
  for (let x = gx0; x <= gx1; x += GRID) {
    ctx.moveTo(x, Math.max(0, view.y0));
    ctx.lineTo(x, Math.min(world.h, view.y1));
  }
  for (let y = gy0; y <= gy1; y += GRID) {
    ctx.moveTo(Math.max(0, view.x0), y);
    ctx.lineTo(Math.min(world.w, view.x1), y);
  }
  ctx.stroke();

  // World edge.
  ctx.lineWidth = 4 / camera.zoom;
  ctx.strokeStyle = "rgba(255,80,80,0.5)";
  ctx.strokeRect(0, 0, world.w, world.h);

  // Entities, bottom layer first, small before big within a layer.
  for (const e of drawOrder(entities, game)) {
    if (e.x + e.r < view.x0 || e.x - e.r > view.x1 || e.y + e.r < view.y0 || e.y - e.r > view.y1) continue;
    const def = game.entities.get(e.type);
    if (!def?.draw) continue;
    ctx.save();
    try {
      def.draw(e, ctx, world, alpha);
    } finally {
      ctx.restore();
    }
  }

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
